import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { IconCirclePlus } from "@tabler/icons";
import EditIcon from "@mui/icons-material/Edit";
import { configState } from "../../../../recoil/atoms";
import LinearProgress from "@mui/material/LinearProgress";
import { useNavigate, useLocation } from 'react-router-dom';
import { InventoryApi } from "../../../../api/medical-operations";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Button, Grid, Typography, Tabs, Tab } from "@mui/material";
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../../utils/FormFields/ConfirmDialog";
import useAuthorization from "../../../Authentication/permissions/Authorization";
import { GetInventoryResponseCollections, GetCollectionItemsResponseInner } from "../../../../api/medical-operations";


interface LocationState {
  InventoryID: string;
}

export default function Inventory() {
  const location = useLocation();
  const navigate = useNavigate();  
  const config = useRecoilValue(configState);

  const [rowID, setRowID] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [columns, setColumns] = useState<MUIDataTableColumnDef[]>([]);
  const [data, setData] = useState<GetCollectionItemsResponseInner[]>([]);

  const [selectedCollectionID, setSelectedCollectionID] = useState<string | null>();
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>();
  const [collections, setCollections] = useState<Array<GetInventoryResponseCollections>>([]);

  const { canEditItem, canAddItem, canDeleteItem } = useAuthorization();
  const inventoryID = location.state ? (location.state as LocationState).InventoryID : '';
  
  const getInventory = () => {
    const inventory = new InventoryApi(config);
    inventory
      .getInventory(inventoryID)
      .then((res) => {
        setIsLoading(false);
        setCollections(res.data.Collections || []);               
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };
  
  const getCollectionItems = (collectionID: string) => {
    const collection = new InventoryApi(config);
    collection
      .getCollectionItems(collectionID)
      .then((res) => {
        setIsLoading(false);
        setData(res.data);                    
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const deleteItem = (id: string) => {
    const Item = new InventoryApi(config);
    Item.deleteInventoryItem(selectedCollectionID!,id)
      .then((res) => {
        getInventory();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getInventory();       
  }, []);

  useEffect(() => {    
    if (collections.length > 0) {
      handleCollectionClick(collections[0]);
    }
  }, [collections]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deleteItem(rowID);
    handleCloseDialog();
  };

  const actions = (customSelectedCollectionID: string, customSelectedCollectionName: string) => ({
    name: "action",
    label: "Action",
    print: false,
    options: {
      filter: false,
      sort: false,
      print: false,
      download: false,
      setCellHeaderProps: () => ({ align: "center" }),
      setCellProps: () => ({ align: "center" }),
      customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
        <>            
          {canEditItem && (
            <Tooltip title="Edit Item">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {    
                  const rowData = tableMeta.tableData[tableMeta.rowIndex];
                
                  navigate(`/edit-item-page/${customSelectedCollectionID}`,{
                    state: {
                      selectedCollectionName: customSelectedCollectionName,
                      rowData
                    }
                  })                                 
                }}
              >
                <EditIcon color="secondary" />
              </Button>
            </Tooltip>
          )}
          {canDeleteItem && (
            <Tooltip title="Delete Item">
              <Button
                variant="outlined"
                color="error"
                sx={{
                  ml: { xs: 0, xl: 2 },
                }}
                onClick={() => {
                  handleDelete(tableMeta.tableData[tableMeta.rowIndex].ItemID);                  
                }}
              >
                <DeleteOutlineIcon color="error" />
              </Button>
            </Tooltip>
          )}
        </>
      ),
    },
  });

  const handleCollectionClick = (selectedCollection: GetInventoryResponseCollections) => {
    const fields = selectedCollection.Fields || [];    
    setSelectedCollectionName(selectedCollection.CollectionName);
    setSelectedCollectionID(selectedCollection.CollectionID);      
    getCollectionItems(selectedCollection.CollectionID || "");   
    
    
    const dynamicColumns: MUIDataTableColumnDef[] = fields.map((field) => ({
      name: field.name || '',
      label: field.name || '', 
    }));
    
    const staticColumn: MUIDataTableColumnDef = {
      name: "FieldID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false
      }
    };    
    const collectionActions = actions(selectedCollection.CollectionID || "", selectedCollection.CollectionName || "");
    setColumns([staticColumn, ...dynamicColumns, collectionActions]); 
    setSelectedCollection(selectedCollection.CollectionID ?? "");
  };

  const renderCollectionNames = () => {
    return (
      <Tabs
        value={selectedCollection}
        onChange={(event, newValue) => {
          const selectedCollection = collections.find(collection => collection.CollectionID === newValue);
          if (selectedCollection) {
            handleCollectionClick(selectedCollection);
          }
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {collections.map((collection) => (
          <Tab
            key={collection.CollectionID}
            label={collection.CollectionName}
            value={collection.CollectionID}
          />
        ))}
      </Tabs>
    );
  };  
  
  return (
    <>
      <MainCard>
        <Grid item container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} style={{ overflowX: "auto", whiteSpace: "nowrap" }}>              
            <Grid item xs={12} style={{ overflowX: "auto" }}>                    
              <Typography style={{ display: "flex", gap: "8px" }}>
                {renderCollectionNames()}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid sx={{ mt: 4 }}>
          {!isLoading ? (
            <>                
              {selectedCollection && (
                <MUIDataTable
                  key={selectedCollection}                    
                  data={data}
                  columns={columns}
                  options={{
                    filter: true,
                    viewColumns: false,
                    selectableRows: "none",
                    selectableRowsOnClick: false,
                    elevation: 0,
                    rowsPerPage: 10,
                    filterType: "dropdown",
                    enableNestedDataAccess: ".",
                    responsive: "standard",
                    customToolbar: () => {
                      return (
                        <>
                          {(canAddItem && (
                              <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<IconCirclePlus />}
                                onClick={() => {
                                  navigate(`/add-item-page/${selectedCollectionID}`,{
                                    state: selectedCollectionName,
                                  })                                                                         
                                }}
                                sx={{
                                ml: "50px",
                                }}
                              >
                                Add Item
                              </Button>
                          ))}
                        </>
                      );
                    },
                  }}
                  title={selectedCollectionName}
                />
              )}  
              <ConfirmDialog
                name={"Item"}
                handleCloseDialog={handleCloseDialog}
                handleConfirm={handleConfirm}
                openDialog={openDialog}
              />                              
            </>
          ) : (
            <LinearProgress color="secondary" />
          )}
        </Grid>
      </MainCard>
    </>
  );
}
